import { Injectable } from '@angular/core';

import { Address } from '../../../../../Domain/Model/address';

import AutocompleteSuggestion = google.maps.places.AutocompleteSuggestion;

@Injectable({
    providedIn: 'root'
})
export class GoogleAutocompleteResultToAddressMapperService {
    async map(
        suggestions: AutocompleteSuggestion[],
        sessionToken: google.maps.places.AutocompleteSessionToken
    ): Promise<Address[]> {
        // Create a PlacesService instance (requires a DOM element, but used only for API calls)
        const placeDetailsService = new google.maps.places.PlacesService(document.createElement('div'));

        const placePredictions = suggestions
            .filter(suggestions => suggestions.placePrediction)
            .map(s => s.placePrediction as unknown as google.maps.places.PlacePrediction);

        // Map each suggestion to a Promise<Address | null>
        const addressPromises: Promise<Address | null>[] = placePredictions.map(async prediction => {
            try {
                const placeDetails = await this.getPlaceDetails(placeDetailsService, prediction.placeId, sessionToken);
                return this.mapSuggestionToAddress(placeDetails);
            } catch (error) {
                console.error(`Failed to fetch details for placeId ${prediction.placeId}:`, error);
                return null; // Return null for failed mappings
            }
        });

        // Wait for all promises to resolve and filter out null results
        const addresses = await Promise.all(addressPromises);
        return addresses.filter(address => address !== null) as Address[];
    }

    private getPlaceDetails(
        service: google.maps.places.PlacesService,
        placeId: string,
        sessionToken: google.maps.places.AutocompleteSessionToken
    ): Promise<google.maps.places.PlaceResult> {
        return new Promise((resolve, reject) => {
            service.getDetails(
                { placeId, fields: ['address_components', 'formatted_address'], sessionToken },
                (result, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && result) {
                        resolve(result);
                    } else {
                        reject(new Error(`Failed to fetch place details: ${status}`));
                    }
                }
            );
        });
    }

    private mapSuggestionToAddress(placeDetails: google.maps.places.PlaceResult): Address {
        const addressComponents = placeDetails.address_components || [];
        const getComponent = (type: string) =>
            addressComponents.find(comp => comp.types.includes(type))?.long_name || '';

        if (!placeDetails.formatted_address) {
            throw new Error('Failed to fetch formatted address');
        }
        return new Address(
            getComponent('street_number'),
            getComponent('route'),
            getComponent('locality') || getComponent('sublocality'), // Fallback to sublocality
            getComponent('administrative_area_level_1'),
            getComponent('postal_code'),
            placeDetails.formatted_address
        );
    }
}
